import { Collection, Scene, Card, SceneWithStats, CollectionWithStats, SceneStats, SceneTopCardsStats } from "../common/types";

const STORAGE_KEY = "ark_credential";

function protectedFetch(input: string, init: RequestInit = {}): Promise<Response> {
  const credential = localStorage.getItem(STORAGE_KEY);
  const headers = new Headers(init.headers);
  if (credential) {
    headers.set("Authorization", `Bearer ${credential}`);
  }
  return fetch(input, { ...init, headers }).then((response) => {
    if (response.status === 401 || response.status === 403) {
      localStorage.removeItem(STORAGE_KEY);
      window.location.href = "/login";
    }
    return response;
  });
}

// Collection APIs
export function listCollections(): Promise<Collection[]> {
  return protectedFetch(`/api/collections`)
    .then((response) => {
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      return response.json();
    })
    .then(response => response.collections);
}

export function getCollection(collectionId: string, from: Date, to: Date): Promise<CollectionWithStats> {
  const params = new URLSearchParams();
  if (from) {
    params.append("from", from.toISOString());
  }
  if (to) {
    params.append("to", to.toISOString());
  }
  return protectedFetch(`/api/collection/${collectionId}?${params.toString()}`)
    .then((response) => {
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      return response.json();
    });
}

export function createCollection(name: string, client: string, description?: string): Promise<Collection> {
  return protectedFetch(`/api/collections`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ name, client, description })
  }).then((response) => {
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    return response.json();
  }).then(response => response.collection);
}

export function updateCollection(collectionId: string, data: Partial<Pick<Collection, 'name' | 'description' | 'client'>>): Promise<Collection> {
  return protectedFetch(`/api/collection/${collectionId}/update`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(data)
  }).then((response) => {
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    return response.json();
  }).then(response => response.collection);
}

// Scene APIs
export function listScenes(limit: number = 10, collectionId?: string): Promise<Scene[]> {
  const params = new URLSearchParams();
  params.append("limit", limit.toString());
  if (collectionId) {
    params.append("collection_id", collectionId);
  }
  return protectedFetch(`/api/scenes?${params.toString()}`)
    .then((response) => {
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      return response.json();
    })
    .then(response => response.scenes);
}

export function getScene(sceneId: string, from: Date, to: Date): Promise<SceneWithStats> {
  const url = `/api/scene/${sceneId}`;
  const params = new URLSearchParams();
  if (from) {
    params.append("from", from.toISOString());
  }
  if (to) {
    params.append("to", to.toISOString());
  }
  return protectedFetch(`${url}?${params.toString()}`)
    .then((response) => {
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      return response.json();
    });
}

export function createScene(collectionId: string, sceneName: string, data?: Partial<Scene>): Promise<Scene> {
  return protectedFetch(`/api/scenes`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ collection_id: collectionId, scene_name: sceneName, ...data })
  }).then((response) => {
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    return response.json();
  }).then(response => response.scene);
}

export function updateScene(sceneId: string, data: Partial<Scene>): Promise<Scene> {
  return protectedFetch(`/api/scene/${sceneId}/update`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(data)
  }).then((response) => {
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    return response.json();
  }).then(response => response.scene);
}

// Card APIs
export function isCardEnabled(cardId: string): Promise<boolean> {
  return protectedFetch(`/api/is_card_enabled/${cardId}`)
    .then((response) => {
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      return response.json();
    })
    .then((data) => data.enabled)
    .catch(() => false);
}

export function createCards(scene_id: string, count: number): Promise<string[]> {
  return protectedFetch(`/api/scene/${scene_id}/create_cards`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ count })
  }).then((response) => {
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    return response.json();
  });
}

export function listCardsForScene(sceneId: string): Promise<Card[]> {
  return protectedFetch(`/api/scene/${sceneId}/cards`)
    .then((response) => {
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      return response.json();
    })
    .then(response => response.cards);
}

export function updateCard(cardId: string, data: Partial<Pick<Card, 'enabled' | 'description'>>): Promise<Card> {
  return protectedFetch(`/api/card/${cardId}/update`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(data)
  }).then((response) => {
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    return response.json();
  }).then(response => response.card);
}

export function getSceneViews(scene_id: string, from?: Date, to?: Date): Promise<SceneStats> {
  const url = `/api/scene/${scene_id}/views`;
  const params = new URLSearchParams();
  if (from) {
    params.append("from", from.toISOString());
  }
  if (to) {
    params.append("to", to.toISOString());
  }
  return protectedFetch(`${url}?${params.toString()}`)
    .then((response) => {
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      return response.json();
    });
}

export function getSceneTopCards(scene_id: string, from?: Date, to?: Date): Promise<SceneTopCardsStats> {
  const url = `/api/${scene_id}/top_viewed_cards`;
  const params = new URLSearchParams();
  if (from) {
    params.append("from", from.toISOString());
  }
  if (to) {
    params.append("to", to.toISOString());
  }
  return protectedFetch(`${url}?${params.toString()}`)
    .then((response) => {
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      return response.json();
    });
}